/**
 * Active play-time tracking.
 *
 * Accumulates only while the level is actually being played: it stops when the game is
 * paused, when the tab is hidden and when the level ends. `performance.now()` is used
 * because it is monotonic and unaffected by clock changes — and it is never allowed near
 * level generation, which must not depend on time at all.
 */
export class ActiveTimer {
  private accumulated = 0;
  private startedAt: number | null = null;

  get running(): boolean {
    return this.startedAt !== null;
  }

  get elapsedMs(): number {
    if (this.startedAt === null) return this.accumulated;
    return this.accumulated + (performance.now() - this.startedAt);
  }

  start(): void {
    if (this.startedAt !== null) return;
    this.startedAt = performance.now();
  }

  pause(): void {
    if (this.startedAt === null) return;
    this.accumulated += performance.now() - this.startedAt;
    this.startedAt = null;
  }

  reset(initialMs = 0): void {
    this.accumulated = initialMs;
    this.startedAt = null;
  }
}

export function formatDuration(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
